<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useTemplateForm } from './useTemplateForm';
import TemplateMetaFields from './TemplateMetaFields.vue';
import TemplateHeaderEditor from './TemplateHeaderEditor.vue';
import TemplateBodyEditor from './TemplateBodyEditor.vue';
import TemplateFooterEditor from './TemplateFooterEditor.vue';
import TemplateButtonsEditor from './TemplateButtonsEditor.vue';
import TemplateVariableSamples from './TemplateVariableSamples.vue';
import TemplatePhonePreview from './TemplatePhonePreview.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
  backLabel: { type: String, default: '' },
  // When provided, the creator operates in edit mode
  template: { type: Object, default: null },
});

const emit = defineEmits(['close', 'back', 'synced']);

const { t } = useI18n();
const alertFn = useAlert;
const currentAccountId = useMapGetter('getCurrentAccountId');

const isEditMode = computed(() => !!props.template);

const {
  name,
  category,
  language,
  headerType,
  headerText,
  headerMediaFile,
  headerMediaPreviewUrl,
  bodyText,
  footerText,
  quickReplies,
  ctaButtons,
  sampleVariables,
  variableTypes,
  isSubmitting,
  fieldErrors,
  canAddQuickReply,
  canAddCta,
  canInsertHeaderVariable,
  headerFileName,
  headerAcceptAttr,
  getAllVariables,
  getPreviewBody,
  getPreviewHeader,
  getPreviewFooter,
  getPreviewQuickReplies,
  getPreviewCtaButtons,
  languageLabels,
  supportedTemplateLanguages,
  insertVariableAtCursor,
  insertHeaderVariable,
  addQuickReply,
  removeQuickReply,
  addCta,
  removeCta,
  onHeaderFileChange,
  clearHeaderFile,
  resetForm,
  onCreate,
  onUpdate,
  loadFromTemplate,
  templateStatus,
  parameterFormat,
  headerExistingHandle,
  authSecurityRecommendation,
  authCodeExpirationMinutes,
  authButtonText,
} = useTemplateForm();

const isAuthTemplate = computed(() => category.value === 'AUTHENTICATION');

// APPROVED → category is locked; anything else (REJECTED/PAUSED) → editable
const categoryEditable = computed(
  () => templateStatus.value?.toUpperCase() !== 'APPROVED'
);

const metaValue = computed({
  get: () => ({
    name: name.value,
    category: category.value,
    language: language.value,
    parameterFormat: parameterFormat.value,
  }),
  set: val => {
    name.value = val.name;
    category.value = val.category;
    language.value = val.language;
    if (val.parameterFormat) parameterFormat.value = val.parameterFormat;
  },
});

const hasErrors = computed(() => Object.keys(fieldErrors).length > 0);

const onBodyInsertVariable = el => {
  if (el) insertVariableAtCursor(el);
};

const onHeaderInsertVariable = el => {
  if (el) insertHeaderVariable(el);
};

const handleSubmit = () => {
  if (isEditMode.value) {
    onUpdate(props.inbox.id, currentAccountId.value, t, alertFn, emit);
  } else {
    onCreate(props.inbox.id, currentAccountId.value, t, alertFn, emit);
  }
};

const handleReset = () => {
  if (isEditMode.value) {
    loadFromTemplate(props.template);
  } else {
    resetForm();
  }
};

const updateQuickReplies = arr => {
  quickReplies.value = arr;
};
const updateCtaButtons = arr => {
  ctaButtons.value = arr;
};

const onSampleVariablesUpdate = val => {
  sampleVariables.value = val;
};

const onVariableTypesUpdate = val => {
  variableTypes.value = val;
};

onMounted(() => {
  if (isEditMode.value) {
    loadFromTemplate(props.template);
  }
});
</script>

<template>
  <!-- h-full so this fills the fixed-height wrapper from TemplatesPage -->
  <div class="flex flex-col h-full bg-n-background">
    <!-- Top bar — plain shrink-0 flex item, never scrolls -->
    <div
      class="shrink-0 flex items-center justify-between px-6 py-3 border-b border-n-weak bg-n-background"
    >
      <!-- Left: back breadcrumb + page title -->
      <div class="flex items-center gap-1.5 min-w-0">
        <button
          v-if="props.backLabel"
          class="flex items-center gap-1 text-sm text-n-slate-10 hover:text-n-slate-12 transition-colors shrink-0"
          @click="emit('back')"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {{ props.backLabel }}
        </button>
        <span v-if="props.backLabel" class="text-n-slate-6 text-sm">/</span>
        <span class="text-sm font-semibold text-n-slate-12 truncate">
          {{
            isEditMode
              ? t('INBOX_MGMT.TEMPLATES.EDIT.TITLE')
              : t('INBOX_MGMT.TEMPLATES.CREATE.TITLE')
          }}
        </span>
      </div>
      <!-- Right: action buttons -->
      <div class="flex items-center gap-2 shrink-0">
        <button
          type="button"
          class="h-8 px-3.5 rounded-lg text-sm outline outline-1 outline-n-weak text-n-slate-11 hover:bg-n-alpha-2"
          @click="handleReset"
        >
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.RESET') }}
        </button>
        <NextButton
          size="sm"
          solid
          blue
          :disabled="isSubmitting"
          :loading="isSubmitting"
          :label="
            isEditMode
              ? t('INBOX_MGMT.TEMPLATES.EDIT.SAVE_BTN')
              : t('INBOX_MGMT.TEMPLATES.CREATE.CREATE_BTN')
          "
          @click="handleSubmit"
        />
      </div>
    </div>

    <!-- Edit mode restriction banner -->
    <div
      v-if="isEditMode && templateStatus?.toUpperCase() === 'APPROVED'"
      class="shrink-0 flex items-start gap-2.5 px-6 py-3 bg-n-amber-2 border-b border-n-amber-4"
    >
      <svg
        class="w-4 h-4 text-n-amber-9 mt-0.5 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
      <div class="min-w-0">
        <p class="text-xs font-medium text-n-amber-11">
          {{ t('INBOX_MGMT.TEMPLATES.EDIT.APPROVED_CONTENT_NOTE') }}
        </p>
        <p class="text-[11px] text-n-amber-9 mt-0.5">
          {{ t('INBOX_MGMT.TEMPLATES.EDIT.RATE_LIMIT_HINT') }}
        </p>
      </div>
    </div>

    <div class="flex flex-1 overflow-hidden">
      <!-- Left compose column — scrolls independently -->
      <div class="flex-[3] overflow-y-auto px-6 pt-5 pb-10 space-y-5 min-w-0">
        <!-- Template Details -->
        <div
          class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4 space-y-4"
        >
          <h3
            class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{
              isEditMode
                ? t('INBOX_MGMT.TEMPLATES.EDIT.TITLE')
                : t('INBOX_MGMT.TEMPLATES.CREATE.TITLE')
            }}
          </h3>
          <TemplateMetaFields
            v-model="metaValue"
            :field-errors="fieldErrors"
            :supported-template-languages="supportedTemplateLanguages"
            :language-labels="languageLabels"
            :edit-mode="isEditMode"
            :category-editable="categoryEditable"
          />
        </div>

        <!-- Header (hidden for AUTHENTICATION — no custom headers allowed) -->
        <div
          v-if="!isAuthTemplate"
          class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4 space-y-4"
        >
          <h3
            class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPE') }}
          </h3>
          <TemplateHeaderEditor
            v-model:header-type="headerType"
            v-model:header-text="headerText"
            :header-media-file="headerMediaFile"
            :header-media-preview-url="headerMediaPreviewUrl"
            :header-accept-attr="headerAcceptAttr"
            :header-file-name="headerFileName"
            :can-insert-header-variable="canInsertHeaderVariable"
            @file-change="onHeaderFileChange"
            @clear-file="clearHeaderFile"
            @insert-header-variable="onHeaderInsertVariable"
          />
          <p
            v-if="
              isEditMode &&
              ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) &&
              !headerExistingHandle
            "
            class="text-[11px] text-n-amber-9 mt-1"
          >
            {{ t('INBOX_MGMT.TEMPLATES.EDIT.MEDIA_REUPLOAD_REQUIRED') }}
          </p>
        </div>

        <!-- Message: Body + Footer (non-authentication only) -->
        <div
          v-if="!isAuthTemplate"
          class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4 space-y-5"
        >
          <h3
            class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.MESSAGE_SECTION') }}
          </h3>
          <TemplateBodyEditor
            v-model="bodyText"
            :sample-variables="sampleVariables"
            :field-errors="fieldErrors"
            @insert-variable="onBodyInsertVariable"
          />
          <div class="border-t border-n-weak pt-4">
            <TemplateFooterEditor
              v-model="footerText"
              :field-errors="fieldErrors"
            />
          </div>
        </div>

        <!-- Buttons (non-authentication only) -->
        <div
          v-if="!isAuthTemplate"
          class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4 space-y-4"
        >
          <h3
            class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.BUTTONS') }}
          </h3>
          <TemplateButtonsEditor
            :quick-replies="quickReplies"
            :cta-buttons="ctaButtons"
            :can-add-quick-reply="canAddQuickReply"
            :can-add-cta="canAddCta"
            :field-errors="fieldErrors"
            @add-quick-reply="addQuickReply"
            @remove-quick-reply="removeQuickReply"
            @update:quick-replies="updateQuickReplies"
            @add-cta="addCta"
            @remove-cta="removeCta"
            @update:cta-buttons="updateCtaButtons"
          />
        </div>

        <!-- AUTHENTICATION options -->
        <div
          v-if="isAuthTemplate"
          class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4 space-y-5"
        >
          <h3
            class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_OPTIONS_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_BODY_HINT') }}
          </p>
          <!-- Security recommendation toggle -->
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="authSecurityRecommendation"
              type="checkbox"
              class="w-4 h-4 rounded accent-n-brand"
            />
            <span class="text-sm text-n-slate-12">
              {{
                t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_SECURITY_RECOMMENDATION')
              }}
            </span>
          </label>
          <!-- Code expiration -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_CODE_EXPIRY_LABEL') }}
            </label>
            <input
              v-model="authCodeExpirationMinutes"
              type="number"
              min="1"
              max="90"
              class="w-32 h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
              :placeholder="
                t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_CODE_EXPIRY_PH')
              "
            />
            <p class="text-[11px] text-n-slate-10 mt-1">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_CODE_EXPIRY_HINT') }}
            </p>
            <p
              v-if="fieldErrors.authExpiry"
              class="text-[11px] text-n-ruby-9 mt-1"
            >
              {{ fieldErrors.authExpiry }}
            </p>
          </div>
          <!-- Copy code button label -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_BUTTON_LABEL') }}
            </label>
            <input
              v-model="authButtonText"
              type="text"
              maxlength="25"
              class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
              :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_BUTTON_PH')"
            />
            <p class="text-[11px] text-n-slate-10 mt-1">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.AUTH_BUTTON_HINT') }}
            </p>
          </div>
        </div>

        <!-- Variable Samples (non-authentication only) -->
        <div
          v-if="!isAuthTemplate && getAllVariables.length > 0"
          class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4 space-y-4"
        >
          <h3
            class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.VARIABLE_SAMPLES_TITLE') }}
          </h3>
          <TemplateVariableSamples
            :all-variables="getAllVariables"
            :sample-variables="sampleVariables"
            :variable-types="variableTypes"
            :header-text="headerText"
            :body-text="bodyText"
            :footer-text="footerText"
            @update:sample-variables="onSampleVariablesUpdate"
            @update:variable-types="onVariableTypesUpdate"
          />
        </div>

        <!-- Validation summary -->
        <div
          v-if="hasErrors"
          class="rounded-xl border border-n-ruby-6 bg-n-ruby-2 px-5 py-4 space-y-2"
        >
          <h3
            class="text-[11px] font-semibold uppercase tracking-wide text-n-ruby-9"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.VALIDATION_ERRORS') }}
          </h3>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(error, key) in fieldErrors"
              :key="key"
              class="text-xs bg-n-ruby-4 text-n-ruby-11 px-2 py-1 rounded-full"
            >
              {{ error }}
            </span>
          </div>
        </div>
      </div>

      <!-- Right preview column — scrolls independently, no sticky needed -->
      <div
        class="flex-[2] border-l border-n-weak bg-[#f0f2f5] dark:bg-n-slate-2 flex flex-col items-center justify-start pt-8 px-4 overflow-y-auto"
      >
        <TemplatePhonePreview
          :preview-header="getPreviewHeader"
          :preview-body="getPreviewBody"
          :preview-footer="getPreviewFooter"
          :header-type="headerType"
          :header-media-preview-url="headerMediaPreviewUrl"
          :header-media-file="headerMediaFile"
          :header-file-name="headerFileName"
          :quick-replies="getPreviewQuickReplies"
          :cta-buttons="getPreviewCtaButtons"
        />
      </div>
    </div>
  </div>
</template>
